import React, { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Upload, X, ToggleRight, ToggleLeft } from "lucide-react";
import { supabase, uploadImage } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { Spinner, EmptyState, ConfirmModal } from "../../components/common/UI";
import toast from "react-hot-toast";

const CATEGORIES = ["Main", "Starters", "BBQ", "Rice", "Bread", "Desserts", "Drinks", "Extras"];

const defaultItem = {
  name: "",
  description: "",
  price: "",
  category: "Main",
  is_available: true,
  is_featured: false,
  sizes: [], // [{name: "Small", price: 200}, {name: "Medium", price: 300}, ...]
};

// const defaultSize = { name: "", price: "" };
const SIZE_PRESETS = ["Small", "Medium", "Large", "Regular", "Family"];

export default function RestaurantMenu() {
  const { user } = useAuth();
  const [restaurant, setRestaurant] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(defaultItem);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activeCategory, setActiveCategory] = useState("All");
  const [hasSizes, setHasSizes] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data: rest } = await supabase
        .from("restaurants")
        .select("id")
        .eq("owner_id", user.id)
        .single();
      if (rest) {
        setRestaurant(rest);
        const { data } = await supabase
          .from("menu_items")
          .select("*")
          .eq("restaurant_id", rest.id)
          .order("category")
          .order("name");
        setItems(data || []);
      }
      setLoading(false);
    };
    load();
  }, [user]);

  const openForm = (item = null) => {
    setEditItem(item);
    const itemData = item ? { ...item, sizes: item.sizes || [] } : defaultItem;
    setForm(itemData);
    setHasSizes(item ? (item.sizes && item.sizes.length > 0) : false);
    setImagePreview(item?.image_url || null);
    setImageFile(null);
    setShowForm(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  // ── Size management ──────────────────────────────────────────────────────
  const addSize = () => {
    setForm((f) => ({ ...f, sizes: [...(f.sizes || []), { name: "", price: "" }] }));
  };

  const updateSize = (index, field, value) => {
    setForm((f) => {
      const sizes = [...(f.sizes || [])];
      sizes[index] = { ...sizes[index], [field]: value };
      return { ...f, sizes };
    });
  };

  const removeSize = (index) => {
    setForm((f) => ({
      ...f,
      sizes: (f.sizes || []).filter((_, i) => i !== index),
    }));
  };

  const toggleSizes = (enabled) => {
    setHasSizes(enabled);
    if (!enabled) setForm((f) => ({ ...f, sizes: [] }));
    else if (!form.sizes || form.sizes.length === 0) addSize();
  };

  // ── Save item ────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Item name is required"); return; }
    if (!hasSizes && !form.price) { toast.error("Price is required"); return; }
    if (hasSizes && form.sizes.some((s) => !s.name || !s.price)) {
      toast.error("Fill in all size names and prices");
      return;
    }

    setSaving(true);
    try {
      let image_url = form.image_url || null;
      if (imageFile) {
        image_url = await uploadImage(
          "menu-images",
          imageFile,
          `${restaurant.id}/${Date.now()}-${imageFile.name}`
        );
      }

      const sizes = hasSizes
        ? form.sizes.map((s) => ({ name: s.name, price: parseFloat(s.price) }))
        : [];

      // Base price: if sizes exist, use lowest size price; otherwise use entered price
      const basePrice = hasSizes
        ? Math.min(...sizes.map((s) => s.price))
        : parseFloat(form.price);

      const payload = {
        ...form,
        price: basePrice,
        sizes,
        image_url,
        restaurant_id: restaurant.id,
      };

      if (editItem) {
        const { error } = await supabase
          .from("menu_items")
          .update(payload)
          .eq("id", editItem.id);
        if (error) throw error;
        setItems((p) => p.map((i) => i.id === editItem.id ? { ...i, ...payload } : i));
        toast.success("Item updated!");
      } else {
        const { data, error } = await supabase
          .from("menu_items")
          .insert(payload)
          .select()
          .single();
        if (error) throw error;
        setItems((p) => [...p, data]);
        toast.success("Item added!");
      }
      setShowForm(false);
    } catch (err) {
      toast.error(err.message || "Failed to save item");
    } finally {
      setSaving(false);
    }
  };

  const toggleAvailable = async (id, current) => {
    await supabase.from("menu_items").update({ is_available: !current }).eq("id", id);
    setItems((p) => p.map((i) => i.id === id ? { ...i, is_available: !current } : i));
  };

  const handleDelete = async () => {
    await supabase.from("menu_items").delete().eq("id", deleteTarget.id);
    setItems((p) => p.filter((i) => i.id !== deleteTarget.id));
    toast.success("Item deleted");
    setDeleteTarget(null);
  };

  const categories = ["All", ...new Set(items.map((i) => i.category))];
  const filteredItems =
    activeCategory === "All" ? items : items.filter((i) => i.category === activeCategory);

  if (loading) return <div className="flex justify-center py-16"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6 page-enter">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Menu Management</h1>
          <p className="text-gray-500 text-sm">{items.length} item(s)</p>
        </div>
        <button onClick={() => openForm()} className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Item
        </button>
      </div>

      {/* Category tabs */}
      {categories.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActiveCategory(c)}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                activeCategory === c
                  ? "bg-blue-600 text-white border-blue-600"
                  : "border-gray-200 text-gray-600 hover:border-blue-300"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {filteredItems.length === 0 ? (
        <EmptyState
          icon="🍳"
          title="No menu items"
          description="Add your first menu item to start receiving orders."
          action={<button onClick={() => openForm()} className="btn-primary">Add First Item</button>}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex"
            >
              <div className="w-24 h-24 flex-shrink-0 bg-gray-100">
                {item.image_url ? (
                  <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-3xl opacity-30">🍛</div>
                )}
              </div>
              <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-gray-900 text-sm truncate">{item.name}</p>
                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full flex-shrink-0">
                      {item.category}
                    </span>
                  </div>
                  {/* Show sizes or single price */}
                  {item.sizes && item.sizes.length > 0 ? (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {item.sizes.map((s, i) => (
                        <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                          {s.name}: Rs {s.price}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="font-bold text-blue-600 text-sm mt-0.5">Rs {item.price?.toLocaleString()}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={() => toggleAvailable(item.id, item.is_available)}
                    className="flex items-center gap-1 text-xs"
                  >
                    {item.is_available
                      ? <ToggleRight className="w-4 h-4 text-green-500" />
                      : <ToggleLeft className="w-4 h-4 text-gray-400" />}
                    <span className={item.is_available ? "text-green-600" : "text-gray-400"}>
                      {item.is_available ? "Available" : "Hidden"}
                    </span>
                  </button>
                  <div className="ml-auto flex gap-1.5">
                    <button
                      onClick={() => openForm(item)}
                      className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(item)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Add/Edit Item Modal ── */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-3xl w-full max-w-md max-h-[92vh] overflow-y-auto animate-slide-up">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="font-display font-bold text-lg text-gray-900">
                {editItem ? "Edit Item" : "Add Menu Item"}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Image upload */}
              <div className="h-32 bg-gray-100 rounded-xl overflow-hidden border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors relative">
                {imagePreview ? (
                  <>
                    <img src={imagePreview} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => { setImagePreview(null); setImageFile(null); setForm((f) => ({ ...f, image_url: null })); }}
                      className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </>
                ) : (
                  <label className="flex flex-col items-center justify-center h-full cursor-pointer text-gray-400 hover:text-blue-500">
                    <Upload className="w-6 h-6 mb-1" />
                    <span className="text-xs">Upload food photo</span>
                    <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
                  </label>
                )}
              </div>

              {/* Name */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                  Item Name *
                </label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                  placeholder="Chicken Biryani"
                  className="input-field"
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                  Description
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={2}
                  placeholder="Describe the item..."
                  className="input-field resize-none"
                />
              </div>

              {/* Category */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                  Category
                </label>
                <select
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  className="input-field"
                >
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Size toggle */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Size Options</p>
                    <p className="text-xs text-gray-500">e.g. Small / Medium / Large</p>
                  </div>
                  <div
                    onClick={() => toggleSizes(!hasSizes)}
                    className={`w-10 h-6 rounded-full transition-colors flex items-center px-1 cursor-pointer ${hasSizes ? "bg-blue-600" : "bg-gray-300"}`}
                  >
                    <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${hasSizes ? "translate-x-4" : "translate-x-0"}`} />
                  </div>
                </div>

                {!hasSizes ? (
                  /* Single price */
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                      Price (Rs) *
                    </label>
                    <input
                      type="number"
                      value={form.price}
                      onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                      min="0"
                      placeholder="250"
                      className="input-field"
                    />
                  </div>
                ) : (
                  /* Size list */
                  <div className="space-y-2">
                    {/* Preset buttons */}
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {SIZE_PRESETS.map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => {
                            if (!form.sizes.find((s) => s.name === preset)) {
                              setForm((f) => ({
                                ...f,
                                sizes: [...(f.sizes || []), { name: preset, price: "" }],
                              }));
                            }
                          }}
                          className="text-xs px-2.5 py-1 bg-blue-50 text-blue-600 border border-blue-200 rounded-full hover:bg-blue-100 transition-colors"
                        >
                          + {preset}
                        </button>
                      ))}
                    </div>

                    {(form.sizes || []).map((size, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <input
                          value={size.name}
                          onChange={(e) => updateSize(index, "name", e.target.value)}
                          placeholder="Size name"
                          className="input-field flex-1 py-2 text-sm"
                        />
                        <input
                          type="number"
                          value={size.price}
                          onChange={(e) => updateSize(index, "price", e.target.value)}
                          placeholder="Price"
                          min="0"
                          className="input-field w-24 py-2 text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => removeSize(index)}
                          className="text-red-400 hover:text-red-600 flex-shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={addSize}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add another size
                    </button>
                  </div>
                )}
              </div>

              {/* Checkboxes */}
              <div className="flex gap-4">
                {[
                  { key: "is_available", label: "Available" },
                  { key: "is_featured", label: "Popular/Featured" },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form[key]}
                      onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.checked }))}
                      className="w-4 h-4 rounded accent-blue-500"
                    />
                    <span className="text-sm text-gray-700">{label}</span>
                  </label>
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 btn-ghost border border-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-2"
                >
                  {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {saving ? "Saving..." : editItem ? "Update Item" : "Add Item"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Menu Item"
        message={`Delete "${deleteTarget?.name}"? This cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        danger
      />
    </div>
  );
}
